import React, { useMemo } from 'react';
import type { RequestOptions } from 'searchkit';
import { useTranslation } from 'react-i18next';
import c from 'classnames';

import type { ImmutableEntity } from '@uncinc/react-drupal-core/types';
import { Container, Text } from '@uncinc/uncinc-react-kitchen-sink';

import type {
  ParagraphContentOverview as Type,
} from '../../../../types/uncinc_profile/paragraphs';

import {
  constructQuery,
  constructAdditionalOverviewFilters,
} from '../../../../components/Search/MetaSearch/metaParagraphsHelpers';
import MetaSearch from '../../../../components/Search/MetaSearch';

import './style.scss';

interface ParagraphContentOverviewProps {
  id?: string;
  entity: ImmutableEntity<Type>;
  isWide?: boolean;
}

const ParagraphContentOverview: React.FC<ParagraphContentOverviewProps> = ({
  id,
  entity,
  isWide = false,
}) => {
  const { t } = useTranslation();
  const contentOverview = entity.toJS();
  const contentType = contentOverview.field_content_type[0].value;
  const withSearchbar = contentOverview.field_enable_searchbar[0]?.value || false;
  const selectionMode = contentOverview.field_selection_mode_overview[0].value;
  const operator =
    (contentOverview.field_query_operator[0]?.value === true && selectionMode === 'advanced')
      ? 'must'
      : 'should';
  const langcode = contentOverview.langcode[0].value;

  const title = contentOverview.field_title[0]?.value;
  const body = contentOverview.field_body[0]?.processed || '';
  const requestOptions: RequestOptions = {
    getBaseFilters: () => [
      constructQuery(contentType, langcode),
      constructAdditionalOverviewFilters(contentOverview, operator),
    ],
  };

  // If no filter fields have more than one value,
  // it's no use showing facets.
  // TODO: Dynamic Facet options so we show only Facets that have
  // more than one option.
  const withFacets = useMemo(() => (
    contentOverview.field_themes?.length > 1 ||
    contentOverview.field_departments?.length > 1 ||
    contentOverview.field_locations?.length > 1 ||
    contentOverview.field_equipment_types?.length > 1 ||
    contentOverview.field_selection_mode_overview[0].value === 'default'
  ), [contentOverview]);

  const classNames = c(
    'Paragraph',
    'ParagraphMeta',
    'ParagraphContentOverview',
    `ParagraphContentOverview--${contentType}`,
  );

  return (
    <div id={id} className={classNames}>
      <Container>
        <div className="row">
          {!title && (
            <h2 className="visually-hidden">
              {t('paragraphs.content_overview.sr_only_title', { content_type: contentType })}
            </h2>
          )}

          {(title || body) && (
            <div className={isWide ? 'col-12 col-md-10 col-lg-8' : 'col-12 col-md-10 offset-md-1 col-lg-8 offset-lg-1'}>
              <div className="ParagraphContentOverview__body">
                <Text
                  title={title}
                  text={body}
                />
              </div>
            </div>
          )}

          <div className={isWide ? 'col-12' : 'col-12 col-md-10 offset-md-1'}>
            <MetaSearch
              requestOptions={requestOptions}
              contentType={contentType}
              hitsPerPage={12}
              withSearchbar={withSearchbar}
              withFacets={withFacets}
              withPagination
            />
          </div>
        </div>
      </Container>
    </div>
  );
};

export default ParagraphContentOverview;
